import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) {
      return savedTheme;
    }
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  });

  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('theme', theme);
  }, [theme]);

  const toggleTheme = useCallback((event) => {
    // Get the click position for the ripple origin
    let x, y;
    if (event && event.clientX !== undefined) {
      x = event.clientX;
      y = event.clientY;
    } else {
      // Fallback to center of the screen
      x = window.innerWidth / 2;
      y = window.innerHeight / 2;
    }

    // Check if the View Transitions API is supported
    if (document.startViewTransition) {
      // Use View Transitions API for a smooth circular clip-path reveal
      document.documentElement.style.setProperty('--ripple-x', `${x}px`);
      document.documentElement.style.setProperty('--ripple-y', `${y}px`);

      const transition = document.startViewTransition(() => {
        setTheme(prev => prev === 'light' ? 'dark' : 'light');
      });

      transition.ready.then(() => {
        // Calculate the maximum distance from the click point to any corner
        const maxDist = Math.hypot(
          Math.max(x, window.innerWidth - x),
          Math.max(y, window.innerHeight - y)
        );

        document.documentElement.animate(
          {
            clipPath: [
              `circle(0px at ${x}px ${y}px)`,
              `circle(${maxDist}px at ${x}px ${y}px)`,
            ],
          },
          {
            duration: 700,
            easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
            pseudoElement: '::view-transition-new(root)',
          }
        );
      });
    } else {
      // Fallback: use manual overlay ripple animation
      setIsAnimating(true);

      const newTheme = theme === 'light' ? 'dark' : 'light';

      // Create the ripple overlay element
      const overlay = document.createElement('div');
      overlay.className = 'theme-ripple-overlay';
      overlay.style.setProperty('--ripple-x', `${x}px`);
      overlay.style.setProperty('--ripple-y', `${y}px`);
      overlay.style.background = newTheme === 'dark' ? '#111827' : '#ffffff';

      document.body.appendChild(overlay);

      // Trigger the animation
      requestAnimationFrame(() => {
        overlay.classList.add('active');
      });

      // Apply the theme change mid-animation
      setTimeout(() => {
        setTheme(newTheme);
      }, 350);

      // Remove the overlay after animation completes
      setTimeout(() => {
        overlay.remove();
        setIsAnimating(false);
      }, 750);
    }
  }, [theme]);

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, isAnimating }}>
      {children}
    </ThemeContext.Provider>
  );
};
